// Hook that detects user inactivity and fires a callback (typically logout)
// after a configurable timeout. Used by App.js for the auto-logout policy.

import { useEffect, useRef, useCallback } from 'react';

// Activity events to track
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
];

// Storage key for last activity timestamp
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

/**
 * Fires `onTimeout` once user activity has been absent for `timeoutMs`.
 * Last-activity is persisted in localStorage so idle time survives reloads.
 *
 * @param {Function} onTimeout Callback when the timeout is reached.
 * @param {number} [timeoutMs] Timeout in milliseconds (default 2 hours).
 * @param {boolean} [enabled] Whether the timeout is active (default true).
 * @returns {{resetActivity: Function}} Manual activity reset, e.g. after API calls.
 */
export function useIdleTimeout(onTimeout, timeoutMs = 2 * 60 * 60 * 1000, enabled = true) {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, []);

  const checkTimeout = useCallback(() => {
    if (!enabled) return;

    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || lastActivityRef.current, 10);
    const now = Date.now();
    const elapsed = now - lastActivity;

    if (elapsed >= timeoutMs) {
      console.log(`[IdleTimeout] User idle for ${Math.round(elapsed / 1000 / 60)} minutes. Logging out.`);
      onTimeout();
    }
  }, [enabled, timeoutMs, onTimeout]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!enabled) return;

    updateActivity();

    timeoutRef.current = setTimeout(() => {
      checkTimeout();
    }, timeoutMs);
  }, [enabled, timeoutMs, updateActivity, checkTimeout]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    updateActivity();

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    resetTimer();

    // Check on mount in case the user was away while the app was closed.
    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10);
    if (lastActivity) {
      const elapsed = Date.now() - lastActivity;
      if (elapsed >= timeoutMs) {
        console.log(`[IdleTimeout] User was idle for ${Math.round(elapsed / 1000 / 60)} minutes. Logging out.`);
        onTimeout();
      }
    }

    // Periodic check catches background tabs where timers are throttled.
    const intervalId = setInterval(() => {
      checkTimeout();
    }, 60 * 1000);

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      clearInterval(intervalId);
    };
  }, [enabled, timeoutMs, handleActivity, resetTimer, updateActivity, checkTimeout, onTimeout]);

  return { resetActivity: updateActivity };
}

export default useIdleTimeout;

